// Header navigation components
// Mobile nav drawer, submenu triggers, mobile nav triggers
class SubmenuTrigger extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.button = null;
  }

  connectedCallback() {
    // Find button inside or use the element itself
    this.button = this.querySelector('button') || this;
    
    if (!this.button) {
      console.warn('SubmenuTrigger: button element is required inside submenu-trigger');
      return;
    }

    const ariaExpanded = this.getAttribute('aria-expanded');
    const ariaControls = this.getAttribute('aria-controls');
    
    if (ariaExpanded && !this.button.hasAttribute('aria-expanded')) {
      this.button.setAttribute('aria-expanded', ariaExpanded);
    }
    if (ariaControls && !this.button.hasAttribute('aria-controls')) {
      this.button.setAttribute('aria-controls', ariaControls);
    }
      
    // Use button for interactions
    this.button.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    if (this.button && this.button.tagName === 'BUTTON') {
      this.button.removeEventListener('click', this.handleClick);
    } else {
      this.removeEventListener('click', this.handleClick);
    }
  }

  handleClick(e) {
    e.preventDefault();
    this.toggle();
  }

  toggle() {
    // Always use button for ARIA attributes (best practice)
    const target = this.button || this;
    const isExpanded = target.getAttribute('aria-expanded') === 'true';
    const controlsId = target.getAttribute('aria-controls');
    
    if (!controlsId) return;

    // Close all other submenus
    this.closeOtherSubmenus(controlsId);

    // Toggle this submenu
    const newExpandedState = !isExpanded;
    
    // Update all buttons that control this submenu (aria-controls is on buttons now)
    const buttonsWithSameId = document.querySelectorAll(
      `button[aria-controls="${controlsId}"]`
    );
    
    buttonsWithSameId.forEach(btn => {
      // Update button's aria-expanded (the interactive element)
      btn.setAttribute('aria-expanded', newExpandedState ? 'true' : 'false');
    });

    // Update the submenu itself
    const submenu = document.getElementById(controlsId);
    if (submenu) {
      submenu.setAttribute('data-active', newExpandedState ? 'true' : 'false');
    }
  }

  closeOtherSubmenus(currentControlsId) {
    // Find all buttons with aria-controls (they're inside submenu-trigger custom elements)
    const allButtons = document.querySelectorAll('submenu-trigger button[aria-controls]');
    allButtons.forEach(btn => {
      const triggerControlsId = btn.getAttribute('aria-controls');
      if (triggerControlsId && triggerControlsId !== currentControlsId) {
        btn.setAttribute('aria-expanded', 'false');
        const submenu = document.getElementById(triggerControlsId);
        if (submenu) {
          submenu.setAttribute('data-active', 'false');
        }
      }
    });
  }
}

class MobileNavTrigger extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.button = null;
  }

  connectedCallback() {
    // Find button inside or use the element itself
    this.button = this.querySelector('button') || this;
    
    if (!this.button) {
      console.warn('MobileNavTrigger: button element is required inside mobile-nav-trigger');
      return;
    }

    this.button.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.handleClick);
    }
  }

  handleClick(e) {
    e.preventDefault();
    this.toggle();
  }

  toggle() {
    const isOpen = document.body.getAttribute('data-nav-open') === 'true';
    const willOpen = !isOpen;
    document.body.setAttribute('data-nav-open', willOpen);

    // Reflect open/closed state on any hamburger trigger button
    document.querySelectorAll('mobile-nav-trigger button[aria-controls="mobile-nav-drawer"]').forEach((btn) => {
      btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });

    // Manage the drawer's dialog visibility + focus behaviour
    const drawer = document.querySelector('mobile-nav-drawer');
    if (drawer) {
      drawer.setAttribute('aria-hidden', willOpen ? 'false' : 'true');
      if (willOpen) {
        drawer.removeAttribute('inert');
        if (typeof drawer.open === 'function') drawer.open(this.button);
      } else {
        drawer.setAttribute('inert', '');
        if (typeof drawer.close === 'function') drawer.close();
      }
    }

    // Reset drawer state when closing (after transition completes)
    if (isOpen) {
      setTimeout(() => {
        this.resetDrawer();
      }, 500);
    }
  }

  resetDrawer() {
    const drawer = document.querySelector('mobile-nav-drawer');
    if (!drawer) return;
    
    drawer.reset();
  }
}

class MobileNavDrawer extends HTMLElement {
  constructor() {
    super();
    this._onKeydown = this._onKeydown.bind(this);
    this._returnFocusTo = null;
  }

  // Called when the drawer opens: trap focus inside and remember where to return focus.
  open(returnFocusTo) {
    this._returnFocusTo = returnFocusTo || null;
    document.addEventListener('keydown', this._onKeydown);
    // Move focus to the first focusable control inside the drawer (the close button).
    const closeBtn = this.querySelector('mobile-nav-trigger button');
    if (closeBtn) requestAnimationFrame(() => closeBtn.focus());
  }

  // Called when the drawer closes: release the focus trap and restore focus to the opener.
  close() {
    document.removeEventListener('keydown', this._onKeydown);
    if (this._returnFocusTo && typeof this._returnFocusTo.focus === 'function') {
      this._returnFocusTo.focus();
    }
    this._returnFocusTo = null;
  }

  _onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      const trigger = document.querySelector('mobile-nav-trigger');
      if (trigger && typeof trigger.toggle === 'function') trigger.toggle();
    } else if (e.key === 'Tab') {
      this._trapFocus(e);
    }
  }

  _trapFocus(e) {
    const focusables = Array.from(
      this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  reset() {
    // Reset all submenu triggers inside the drawer
    const submenuTriggers = this.querySelectorAll('submenu-trigger');
    submenuTriggers.forEach(trigger => {
      const btn = trigger.querySelector('button') || trigger;
      btn.setAttribute('aria-expanded', 'false');
    });
    
    // Reset all accordion triggers inside the drawer
    const accordionTriggers = this.querySelectorAll('accordion-trigger');
    accordionTriggers.forEach(trigger => {
      const btn = trigger.querySelector('button') || trigger;
      btn.setAttribute('aria-expanded', 'false');
    });
    
    // Reset all data-active states inside the drawer
    const activeElements = this.querySelectorAll('[data-active]');
    activeElements.forEach(element => {
      element.setAttribute('data-active', 'false');
    });
  }
}

// Register header navigation components
customElements.define('submenu-trigger', SubmenuTrigger);
customElements.define('mobile-nav-trigger', MobileNavTrigger);
customElements.define('mobile-nav-drawer', MobileNavDrawer);

// Header height management
// Sets --header-height to the full height of the header group, which includes
// the announcement bar and any other sections placed above the main nav.
// ResizeObserver is used instead of a one-shot DOMContentLoaded measurement so
// that dynamic changes — such as the announcement-bar custom element upgrading
// after this module loads, or viewport resizes — are always reflected.
function setHeaderHeight() {
  const header = document.querySelector('header');
  if (!header) return;
  document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
};

const _header = document.querySelector('header');

if (_header && typeof ResizeObserver !== 'undefined') {
  new ResizeObserver(setHeaderHeight).observe(_header);
} else {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setHeaderHeight);
  } else {
    setHeaderHeight();
  }
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(setHeaderHeight, 150);
  });
};

document.addEventListener('header:height-change', setHeaderHeight);

// Header appearance controller
// - Transparent (white) at the top of the page; solid (white bg / dark text)
//   once the page is scrolled or a desktop mega-menu is open.
// - Dims the rest of the page with a scrim while a mega-menu is open.
// - Optionally sticky: pinned to the top of the viewport at all times.
// State is driven by toggling literal Tailwind utility classes / data
// attributes — no styling lives in CSS files.
// Sticky is applied to the section's .shopify-section wrapper (a direct child
// of <body>), NOT the inner <header>: a sticky element can only travel within
// its own parent's box, and the wrapper is only as tall as the header, so
// sticking the <header> alone would let it scroll away almost immediately.
class HeaderController {
  constructor(header) {
    this.header = header;
    this.transparent = header.dataset.headerTransparent === 'true';
    this.stickyEnabled = header.dataset.headerSticky === 'true';
    this.scrim = document.querySelector('[data-header-scrim]');
    this.active = new Set();
    this.scrolled = false;
    this._closeTimer = null;
    this._dismissing = false;
    this._onScroll = this._onScroll.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this.init();
  }

  init() {
    if (this.stickyEnabled) {
      const wrapper = this.header.closest('.shopify-section') || this.header.parentElement;
      if (wrapper) wrapper.classList.add('sticky', 'top-0', 'z-[999]');
    }

    // Desktop mega-menu triggers open the solid state + scrim.
    this.header.querySelectorAll('[data-has-megamenu]').forEach((item) => {
      item.addEventListener('pointerenter', () => this._open(item));
      item.addEventListener('pointerleave', () => {
        // Leaving with the pointer clears any Escape-dismissal so a later
        // re-hover reopens normally.
        item.removeAttribute('data-dismissed');
        // Keep open if keyboard focus is still inside (mirrors CSS focus-within).
        if (!item.contains(document.activeElement)) this._close(item);
      });
      item.addEventListener('focusin', () => this._open(item));
      item.addEventListener('focusout', (event) => {
        // Keep open if the pointer is still hovering (mirrors CSS :hover).
        if (!item.contains(event.relatedTarget) && !item.matches(':hover')) {
          item.removeAttribute('data-dismissed');
          this._close(item);
        }
      });
    });

    // Escape dismisses an open desktop mega-menu (panel + scrim) without moving
    // the pointer or focus away — WCAG 2.1 AA 1.4.13 (Content on Hover or Focus,
    // Dismissable). The panel is otherwise hover/focus-driven in CSS, so a
    // data-dismissed flag on the item force-hides it until the pointer/focus
    // genuinely leaves and returns.
    document.addEventListener('keydown', this._onKeydown);

    // Scroll only needs watching when transparency drives the solid swap.
    if (this.transparent) {
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }

    this._onScroll();
    this._render();
  }

  // Detach global listeners so a controller whose header the customizer has
  // since replaced stops reacting to scroll on a now-detached element.
  destroy() {
    window.removeEventListener('scroll', this._onScroll);
    document.removeEventListener('keydown', this._onKeydown);
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
  }

  _open(item) {
    // Ignore the focusin that fires while we move focus back to the trigger
    // during an Escape dismissal — otherwise it would immediately reopen.
    if (this._dismissing) return;
    item.removeAttribute('data-dismissed');
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
    this.active.add(item);
    this._setExpanded(item, true);
    this._render();
  }

  // Escape: close every open mega-menu, force-hide its panel via data-dismissed,
  // and return focus to the trigger of the item the user was in.
  _onKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    if (!this.menuOpen) return;
    event.preventDefault();
    const items = Array.from(this.active);
    // Only pull focus back to a trigger when the user actually had keyboard
    // focus inside the menu; a mouse-hover Escape just dismisses in place.
    const focusedItem = items.find((item) => item.contains(document.activeElement));
    this._dismissing = true;
    items.forEach((item) => {
      item.setAttribute('data-dismissed', 'true');
      this._close(item);
    });
    if (focusedItem) {
      const trigger = focusedItem.querySelector('a[aria-expanded]');
      if (trigger) trigger.focus();
    }
    this._dismissing = false;
  }

  _close(item) {
    this.active.delete(item);
    this._setExpanded(item, false);
    // Small delay smooths over moving between a trigger and its panel.
    if (this._closeTimer) clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => this._render(), 80);
  }

  _setExpanded(item, expanded) {
    const trigger = item.querySelector('a[aria-expanded]');
    if (trigger) trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  _onScroll() {
    if (!this.transparent) return;
    const scrolled = window.scrollY > 8;
    if (scrolled !== this.scrolled) {
      this.scrolled = scrolled;
      this._render();
    }
  }

  get menuOpen() {
    return this.active.size > 0;
  }

  _render() {
    const solid = !this.transparent || this.scrolled || this.menuOpen;
    this.header.setAttribute('data-solid', solid ? 'true' : 'false');
    if (this.scrim) {
      this.scrim.setAttribute('data-active', this.menuOpen ? 'true' : 'false');
    }
  }
}

let _amicciHeaderController = null;

// (Re)initialise the header appearance controller. Unlike the custom elements
// in this theme (announcement bar, drawer) which auto-upgrade via
// connectedCallback, this plain controller is set up once when the module
// loads. The theme customizer swaps a section's HTML via AJAX without
// re-running the module, so a one-shot init would leave a reloaded header with
// no controller — stuck in its initial transparent/white state and invisible
// over the light editor canvas. Guarded + idempotent so it can re-run safely on
// section load, tearing down any stale instance first.
function initAmicciHeader(scope) {
  const header = (scope || document).querySelector('header[data-header]');
  if (!header || header.__amicciHeaderInit) return;
  if (_amicciHeaderController) _amicciHeaderController.destroy();
  _amicciHeaderController = new HeaderController(header);
  header.__amicciHeaderInit = true;
  setHeaderHeight();
}

initAmicciHeader();

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initAmicciHeader(event.target));
}